<script setup lang="ts">
import { computed, watchEffect } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

import { Plus } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import MapSection from './MapSection.vue'

const store = useStore()
const router = useRouter()

const selectedOrgId = computed(() => store.getters['org/getSelected'])
const selectedOrg = computed(() => store.getters['org/getSelectedOrg'])

// getAllMaps, not getMaps: includes the derived Unassigned map so a layer that no
// persisted map claims is still listed instead of silently disappearing.
const maps = computed(() => store.getters['maps/getAllMaps'](selectedOrgId.value))

const openCreateMap = () => {
  router.push({ name: 'MapCreate', params: { org_id: selectedOrgId.value } })
}
// The canvas basemap is now driven by MapCanvas from the active map object.

// Cross-map move: a top-level layer dropped onto a different Map section moves
// into that map. LayerList's own monitor ignores Map-typed drop targets, so the
// two monitors don't fight.
watchEffect((onCleanup) => {
  const orgId = selectedOrgId.value
  if (!orgId) return
  const cleanup = monitorForElements({
    onDrop({ location, source }) {
      if (source.data.type === 'mapCard') return // map cards are handled by MapGrid
      if (source.data.parentUuid) return // only move top-level layers
      const sourceCode = source.data.id as string
      const mapTarget = location.current.dropTargets.find((t: any) => t.data?.type === 'map')
      if (!mapTarget) return
      const targetMapId = mapTarget.data.mapId as string
      // Skip only when the layer already lives solely on the target map. If it's
      // present in several maps (stale duplication), let the move run so the
      // backend can reassign it to exactly one map.
      const owners = store.getters['maps/getMaps'](orgId)
        .filter((m: any) => m.layerCodes.includes(sourceCode))
      if (owners.length === 1 && owners[0].id === targetMapId) return
      store.dispatch('maps/moveLayerToMap', { orgId, layerCode: sourceCode, mapId: targetMapId })
    },
  })
  onCleanup(() => cleanup())
})
</script>

<template>
  <div v-if="selectedOrg" :key="selectedOrg.organization_id" class="px-1">
    <div class="px-1 pb-1.5 flex items-center justify-end">
      <Button class="px-2" variant="ghost" size="sm" @click="openCreateMap">
        <Plus class="text-muted-foreground w-4 h-4 mr-1" />
        New map
      </Button>
    </div>

    <div class="space-y-1">
      <MapSection
        v-for="m in maps"
        :key="m.id"
        :map="m"
        :org-id="selectedOrgId"
      />
    </div>
  </div>
</template>
